/**
 * Collection helpers for cloud-ready data management
 * Provides CRUD operations with automatic metadata and sync queuing
 * 
 * CRITICAL: All new modules should use these helpers for data operations
 * This ensures proper metadata, soft deletes, and sync readiness
 */

import { readJSON, writeJSON } from './storageRouter';
import { genId, genTimestamp } from './id';
import { pushOutbox, CHANGE_TYPES } from './outbox';
import { getContext as getAuthContext } from './context';

/**
 * Get current context (from centralized context shim)
 */
const getContext = () => {
  const authContext = getAuthContext();
  return {
    vesselId: authContext.vesselId,
    companyId: authContext.companyId,
    userId: authContext.userId,
    timestamp: genTimestamp()
  };
};

/**
 * Read a collection from storage
 * @param {string} collectionKey - The storage key for the collection
 * @returns {Array} The collection array
 */
export const readCollection = async (collectionKey) => {
  return await readJSON(collectionKey, []);
};

/**
 * Write a collection to storage
 * @param {string} collectionKey - The storage key for the collection
 * @param {Array} collection - The collection to write
 */
const writeCollection = async (collectionKey, collection) => {
  return await writeJSON(collectionKey, collection);
};

/**
 * Upsert (insert or update) a record in a collection
 * @param {string} collectionKey - The storage key for the collection
 * @param {Object} record - The record to upsert
 * @returns {Object} The complete record with metadata
 */
export const upsert = async (collectionKey, record) => {
  const context = getContext();
  const collection = await readCollection(collectionKey);
  
  // Guard against orphan records - ensure we have a valid vessel context
  if (!context.vesselId || context.vesselId === 'default') {

    // In production, we might want to throw an error here
    // For now, we'll allow it but log the warning
  }
  
  // Validate that vesselId is provided (either from record or context)
  const vesselId = record.vesselId || context.vesselId;
  if (!vesselId) {
    throw new Error('Cannot save record without vesselId. Ensure vessel context is set.');
  }
  
  // Check if record exists
  const existingIndex = collection.findIndex(r => r.id === record.id);
  const isUpdate = existingIndex !== -1;
  
  // Build complete record with metadata
  const completeRecord = {
    ...record,
    id: record.id || genId(),
    vesselId: vesselId, // Use validated vesselId
    companyId: record.companyId || context.companyId,
    createdAt: isUpdate ? collection[existingIndex].createdAt : context.timestamp,
    updatedAt: context.timestamp,
    updatedBy: context.userId,
    deleted: record.deleted || false
  };
  
  // Update or add to collection
  if (isUpdate) {
    collection[existingIndex] = completeRecord;
  } else {
    collection.push(completeRecord);
  }

  // Write to storage
  await writeCollection(collectionKey, collection);

  // Queue for sync
  pushOutbox({
    collection: collectionKey,
    type: isUpdate ? CHANGE_TYPES.UPDATE : CHANGE_TYPES.CREATE,
    recordId: completeRecord.id,
    data: completeRecord,
    vesselId: completeRecord.vesselId
  });
  
  return completeRecord;
};

/**
 * Find a single record in a collection
 * @param {string} collectionKey - The storage key for the collection
 * @param {Function|string} predicate - Function to test records or ID string
 * @returns {Object|null} The found record or null
 */
export const find = async (collectionKey, predicate) => {
  const collection = await readCollection(collectionKey);
  
  // If predicate is a string, treat it as an ID
  if (typeof predicate === 'string') {
    return collection.find(r => r.id === predicate && !r.deleted) || null;
  }
  
  // Otherwise use predicate function
  return collection.find(r => !r.deleted && predicate(r)) || null;
};

/**
 * Filter records in a collection
 * @param {string} collectionKey - The storage key for the collection
 * @param {Function} predicate - Function to test records
 * @returns {Array} Filtered records
 */
export const filter = async (collectionKey, predicate = () => true) => {
  const collection = await readCollection(collectionKey);
  return collection.filter(r => !r.deleted && predicate(r));
};

/**
 * Filter records by vessel
 * @param {string} collectionKey - The storage key for the collection
 * @param {string} vesselId - The vessel ID to filter by
 * @returns {Array} Filtered records
 */
export const filterByVessel = async (collectionKey, vesselId) => {
  return await filter(collectionKey, r => r.vesselId === vesselId);
};

/**
 * Soft delete a record
 * @param {string} collectionKey - The storage key for the collection
 * @param {string} id - The ID of the record to delete
 * @returns {boolean} Success status
 */
export const softDelete = async (collectionKey, id) => {
  const context = getContext();
  const collection = await readCollection(collectionKey);
  const record = collection.find(r => r.id === id);
  
  if (!record) return false;
  
  // Mark as deleted
  record.deleted = true;
  record.deletedAt = context.timestamp;
  record.deletedBy = context.userId;
  record.updatedAt = context.timestamp;

  // Write to storage
  await writeCollection(collectionKey, collection);

  // Queue for sync
  pushOutbox({
    collection: collectionKey,
    type: CHANGE_TYPES.DELETE,
    recordId: id,
    data: record,
    vesselId: record.vesselId
  });
  
  return true;
};

/**
 * Restore a soft-deleted record
 * @param {string} collectionKey - The storage key for the collection
 * @param {string} id - The ID of the record to restore
 * @returns {boolean} Success status
 */
export const restore = async (collectionKey, id) => {
  const context = getContext();
  const collection = await readCollection(collectionKey);
  const record = collection.find(r => r.id === id);
  
  if (!record || !record.deleted) return false;
  
  // Restore record
  record.deleted = false;
  delete record.deletedAt;
  delete record.deletedBy;
  record.updatedAt = context.timestamp;
  record.updatedBy = context.userId;

  // Write to storage
  await writeCollection(collectionKey, collection);

  // Queue for sync
  pushOutbox({
    collection: collectionKey,
    type: CHANGE_TYPES.UPDATE,
    recordId: id,
    data: record,
    vesselId: record.vesselId
  });
  
  return true;
};

/**
 * Count records in a collection
 * @param {string} collectionKey - The storage key for the collection
 * @param {Function} predicate - Optional filter function
 * @returns {number} Count of records
 */
export const count = async (collectionKey, predicate = () => true) => {
  const filtered = await filter(collectionKey, predicate);
  return filtered.length;
};

/**
 * Check if any record matches predicate
 * @param {string} collectionKey - The storage key for the collection
 * @param {Function} predicate - Function to test records
 * @returns {boolean} True if any record matches
 */
export const exists = async (collectionKey, predicate) => {
  const filtered = await filter(collectionKey, predicate);
  return filtered.length > 0;
};

/**
 * Get the most recent records
 * @param {string} collectionKey - The storage key for the collection
 * @param {number} limit - Number of records to return
 * @param {Function} predicate - Optional filter function
 * @returns {Array} Recent records
 */
export const recent = async (collectionKey, limit = 10, predicate = () => true) => {
  const filtered = await filter(collectionKey, predicate);
  return filtered
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .slice(0, limit);
};

/**
 * Bulk upsert multiple records
 * @param {string} collectionKey - The storage key for the collection
 * @param {Array} records - Array of records to upsert
 * @returns {Array} The complete records with metadata
 */
export const bulkUpsert = async (collectionKey, records) => {
  const context = getContext();
  const collection = await readCollection(collectionKey);
  const results = [];
  
  records.forEach(record => {
    const existingIndex = collection.findIndex(r => r.id === record.id);
    const isUpdate = existingIndex !== -1;
    
    const completeRecord = {
      ...record,
      id: record.id || genId(),
      vesselId: record.vesselId || context.vesselId,
      companyId: record.companyId || context.companyId,
      createdAt: isUpdate ? collection[existingIndex].createdAt : context.timestamp,
      updatedAt: context.timestamp,
      updatedBy: context.userId,
      deleted: record.deleted || false
    };
    
    if (isUpdate) {
      collection[existingIndex] = completeRecord;
    } else {
      collection.push(completeRecord);
    }
    
    results.push(completeRecord);
  });

  // Write to storage
  await writeCollection(collectionKey, collection);

  // Queue for sync
  pushOutbox({
    collection: collectionKey,
    type: CHANGE_TYPES.BULK,
    recordId: 'bulk',
    data: results,
    vesselId: context.vesselId
  });
  
  return results;
};

/**
 * Clear all records for a vessel (soft delete)
 * @param {string} collectionKey - The storage key for the collection
 * @param {string} vesselId - The vessel ID
 * @returns {number} Number of records deleted
 */
export const clearVesselData = async (collectionKey, vesselId) => {
  const records = await filterByVessel(collectionKey, vesselId);
  await Promise.all(records.map(record => softDelete(collectionKey, record.id)));
  return records.length;
};